import api from '../httpClient';

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface NotificationChannels {
  inApp: boolean;
  push: boolean;
  email: boolean;
}

export interface NotificationCta {
  label?: string | null;
  destination?: string | null;
  params?: Record<string, unknown> | null;
}

export interface AppNotification {
  id: string;
  recipientType: 'client' | 'nutritionist' | 'admin';
  recipientId: string;
  actorType?: string | null;
  actorId?: string | null;
  eventKey: string;
  workflow: string;
  priority: NotificationPriority;
  title: string;
  body: string;
  cta?: NotificationCta | null;
  source?: {
    type: string;
    id: string;
  } | null;
  channels: NotificationChannels;
  metadata?: Record<string, unknown> | null;
  readAt: string | null;
  archivedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  isRead: boolean;
  isArchived: boolean;
}

export interface NotificationsListResponse {
  items: AppNotification[];
  unreadCount: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface NotificationSummaryResponse {
  unreadCount: number;
  byPriority: Record<NotificationPriority, number>;
}

export interface NotificationPreference {
  eventKey: string;
  workflow: string;
  priority: NotificationPriority;
  eventLabel: string;
  description: string;
  body: string;
  channels: NotificationChannels;
  defaults: NotificationChannels;
  updatedAt: string | null;
}

export interface NotificationPreferencesResponse {
  items: NotificationPreference[];
}

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
}

function unwrapData<T>(payload: ApiEnvelope<T> | T): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiEnvelope<T>).data as T;
  }
  return payload as T;
}

export async function getNotifications(params: {
  status?: 'all' | 'unread' | 'read' | 'archived';
  page?: number;
  limit?: number;
} = {}): Promise<NotificationsListResponse> {
  const response = await api.get<ApiEnvelope<NotificationsListResponse> | NotificationsListResponse>(
    '/notifications',
    { params }
  );
  return unwrapData<NotificationsListResponse>(response.data);
}

export async function getNotificationSummary(): Promise<NotificationSummaryResponse> {
  const response = await api.get<ApiEnvelope<NotificationSummaryResponse> | NotificationSummaryResponse>(
    '/notifications/summary'
  );
  return unwrapData<NotificationSummaryResponse>(response.data);
}

export async function getNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  const response = await api.get<ApiEnvelope<NotificationPreferencesResponse> | NotificationPreferencesResponse>(
    '/notifications/preferences'
  );
  return unwrapData<NotificationPreferencesResponse>(response.data);
}

export async function updateNotificationPreference(input: {
  eventKey: string;
  channels: Partial<NotificationChannels>;
}): Promise<NotificationPreference> {
  const response = await api.patch<ApiEnvelope<NotificationPreference> | NotificationPreference>(
    '/notifications/preferences',
    input
  );
  return unwrapData<NotificationPreference>(response.data);
}

export async function markNotificationRead(id: string): Promise<AppNotification> {
  const response = await api.patch<ApiEnvelope<AppNotification> | AppNotification>(
    `/notifications/${id}/read`
  );
  return unwrapData<AppNotification>(response.data);
}

export async function markAllNotificationsRead(): Promise<{ modifiedCount: number }> {
  const response = await api.patch<ApiEnvelope<{ modifiedCount: number }> | { modifiedCount: number }>(
    '/notifications/read-all'
  );
  return unwrapData<{ modifiedCount: number }>(response.data);
}

export async function archiveNotification(id: string): Promise<AppNotification> {
  const response = await api.patch<ApiEnvelope<AppNotification> | AppNotification>(
    `/notifications/${id}/archive`
  );
  return unwrapData<AppNotification>(response.data);
}
